// Spine (spine-webgl) ランタイム — ボス用。ブラウザ専用 (Vitest からは import しない)。
//
// 🔴 2026-09-01 ユーザー裁定: **ボスを骨格アニメで作る方式は不採用**。
//   ボスは今までどおり「キーフレームごとに絵を生成して切り替える」方式で作る。
//   このファイルは**ラボ限定の実験の残骸**として残置している (既定OFF・配布版に影響ゼロ)。
//   🔴 **これを土台に新しいボスをリグする作業へ入らない。** 理由と経緯 =
//   docs/reports/2026-09-01-boss-spine-mesh-experiment-closeout.md (台帳 2026-09-01 行)。
//   要点 = 描画 (骨の自動配置・変形メッシュ) は成立したが、**動きの中身をAIが想像で作る工程**が
//   成立しなかった。新ボスには参考の絵が無いため、そこが常に律速になる。
//   🔵 主人公 LUNAIRE の Spine (src/spine_player.ts) は現役。この裁定はボスの話。
//
// 実験スコープ: 王(dream-hound)の houndBite だけを骨格アニメで描く経路。
//   背景 = docs/handoffs/2026-08-31-boss-spine-auto-rig-experiment-kickoff.md (🔴 閉じ印済み)
//   拍の正本 = src/boss_pose_timeline.ts の houndBite タイムライン (telegraph 0.4 溜め保持 →
//   active 0.0 接触 → recovery 0.3 follow-through) と src/attacks.ts の houndBite
//   (chargeMs650/activeMs140/recoveryMs560=合計1.35s)。Spine 側 attack アニメも総尺1.35s で作られており、
//   ここではその絶対時刻へ直接写像する (ゲーム側の拍は一切変更しない)。
//
// 🔴 2026-09-01: spine-canvas から spine-webgl へ移した。理由は「できたら良いな」ではなく必然:
//   Canvas2D のバックエンドは**メッシュを描けない** (triangleRendering=false でしか動かない)。
//   完成画を剛体パーツへ切り分ける方式は、休止と idle では完璧でも、振れ幅に比例して崩れる
//   (パーツが動いてどいた場所には、元の絵に存在しない画素が要るため。跳びかかりの頂点で画面の 17.8%)。
//   のりしろ幅・曲げ量・分割の有無を全部実測して、どれでも直らないことを確認済み。
//   正本 = docs/departments/art/spine-quadruped-rigging-notes.md §4c / §4d
//   メッシュは1枚の見本画を面ごと曲げるので、どく場所が生まれない = 存在しない画素が要らない。
//
// src/spine_player.ts (LUNAIRE 用) はモジュール変数のシングルトンで1体専用のため、
//   複数ボスを切り替えうるこちらは bossId ごとに Map で保持する新規ファイルとして分離した。
import {
  AssetManager, AtlasAttachmentLoader, SkeletonJson, Skeleton,
  AnimationState, AnimationStateData, SceneRenderer, ManagedWebGLRenderingContext,
  Physics, TextureAtlas,
} from '@esotericsoftware/spine-webgl'
import { phaseProgressOf } from './boss_pose_timeline'
import type { Boss } from './types'

// オフスクリーンを実寸の何倍で描くか。2D 側へ縮小して貼るのでエッジが締まる。
const SUPERSAMPLE = 2
// 足元をオフスクリーンのどこへ置くか ((0,0)=左上、高さの比率)
const FEET_Y_RATIO = 0.85

interface BossSpineRig {
  skeleton: Skeleton
  state: AnimationState
  scale: number
  currentAnim: string | null
}

interface BossSpineEntry {
  assetManager: AssetManager
  pendingBuild: { jsonPath: string; atlasPath: string }
  targetHeightPx: number
  rig: BossSpineRig | null
}

const entries = new Map<string, BossSpineEntry>()

/** WebGL の描画先はボス間で共有する (コンテキスト数はブラウザ側で上限があるため)。 */
interface Surface {
  canvas: HTMLCanvasElement
  gl: WebGLRenderingContext
  context: ManagedWebGLRenderingContext
  renderer: SceneRenderer
}
let surface: Surface | null = null

function getSurface(): Surface | null {
  if (surface) return surface
  if (typeof document === 'undefined') return null
  const canvas = document.createElement('canvas')
  canvas.width = 16
  canvas.height = 16
  // pma:false のアトラス。canvas 側も非乗算にしておかないと 2D へ貼った時に色が濁る。
  const gl = (canvas.getContext('webgl2', { alpha: true, premultipliedAlpha: false })
    || canvas.getContext('webgl', { alpha: true, premultipliedAlpha: false })) as WebGLRenderingContext | null
  if (!gl) return null
  const context = new ManagedWebGLRenderingContext(gl)
  surface = { canvas, gl, context, renderer: new SceneRenderer(canvas, context) }
  return surface
}

/**
 * ボス用 Spine スケルトンの読み込みを開始する。pathPrefix 配下に json/atlas/png がある前提。
 * 同じ bossId の二重呼び出しは無視する (preloadSpinePlayer と同じ規約)。
 */
export function preloadBossSpineRig(
  bossId: string, pathPrefix: string, jsonFile: string, atlasFile: string, targetHeightPx: number,
): void {
  if (entries.has(bossId)) return
  const s = getSurface()
  if (!s) return
  const assetManager = new AssetManager(s.context, pathPrefix)
  assetManager.loadText(jsonFile)
  assetManager.loadTextureAtlas(atlasFile)
  entries.set(bossId, {
    assetManager,
    pendingBuild: { jsonPath: jsonFile, atlasPath: atlasFile },
    targetHeightPx,
    rig: null,
  })
}

/** 読み込み完了時に一度だけ Skeleton/AnimationState を構築する。 */
function tryBuild(entry: BossSpineEntry): void {
  if (entry.rig || !entry.assetManager.isLoadingComplete()) return

  const atlas = entry.assetManager.get(entry.pendingBuild.atlasPath) as TextureAtlas
  const skeletonJson = new SkeletonJson(new AtlasAttachmentLoader(atlas))
  const skeletonData = skeletonJson.readSkeletonData(entry.assetManager.get(entry.pendingBuild.jsonPath) as string)

  const skeleton = new Skeleton(skeletonData)
  // スケール基準 = JSON の宣言高さ (2026-09-01 に実ランタイムで skeletonData.height=950 を実測確認)。
  //   宣言値が 0 で返る環境に備えて targetHeightPx へフォールバックする。
  const h = skeletonData.height || entry.targetHeightPx
  const scale = entry.targetHeightPx / h

  const state = new AnimationState(new AnimationStateData(skeletonData))
  entry.rig = { skeleton, state, scale, currentAnim: null }
}

/**
 * boss の現在の (phase, progress) から、王の Spine アニメ名+絶対時刻を解決する。
 *   houndBite だけの実験対象なので、他の技・他の phase は null (呼び出し側は既存スプライトへ
 *   フォールバックすること)。
 */
export function resolveBossSpineAnim(boss: Boss): { name: string; time: number; loop: boolean } | null {
  const { phase, progress } = phaseProgressOf(boss)
  if (phase === 'idle') {
    // phaseProgressOf は staggered/blink/reappear 等も 'idle' へ畳むため、本当に idle の時だけ通す。
    if (boss.state !== 'idle') return null
    return { name: 'idle', time: boss.stateElapsed % 2.4, loop: true }
  }
  if (phase !== 'telegraph' && phase !== 'active' && phase !== 'recovery') return null // comboGap 等
  if (boss.currentAttack?.id !== 'houndBite') return null // houndBite 以外は既存絵のまま
  if (phase === 'telegraph') return { name: 'attack', time: progress * 0.65, loop: false }
  if (phase === 'active') return { name: 'attack', time: 0.65 + progress * 0.14, loop: false }
  return { name: 'attack', time: 0.79 + progress * 0.56, loop: false } // recovery
}

/**
 * Spine 経路でボスを描画する。呼び出し側 (drawBoss) は ctx を足元原点へ translate 済み・
 * facing 反転済みで渡すこと。
 *
 * WebGL のオフスクリーンへ描いてから 2D の ctx へ貼る。ctx 側の transform (facing 反転) は
 * drawImage がそのまま効かせてくれるので、ここでは Y 反転をしない (spine-canvas 時代との違い)。
 * @returns 描画できたら true。未ロード・対象外の技/phase なら false (フォールバックさせる)。
 */
export function drawBossSpineIfReady(ctx: CanvasRenderingContext2D, boss: Boss): boolean {
  const entry = entries.get(boss.id)
  if (!entry) return false
  const s = getSurface()
  if (!s) return false
  tryBuild(entry)
  if (!entry.rig) return false
  const rig = entry.rig

  const resolved = resolveBossSpineAnim(boss)
  if (!resolved) return false
  if (!rig.skeleton.data.findAnimation(resolved.name)) return false

  if (rig.currentAnim !== resolved.name) {
    rig.state.setAnimation(0, resolved.name, resolved.loop)
    rig.currentAnim = resolved.name
  }
  const track = rig.state.getTrack(0)
  if (track) track.trackTime = Math.max(0, resolved.time)
  rig.state.apply(rig.skeleton)
  rig.skeleton.x = 0
  rig.skeleton.y = 0
  const draw = rig.scale * SUPERSAMPLE
  rig.skeleton.scaleX = draw
  rig.skeleton.scaleY = draw
  // 4.3 では updateWorldTransform は Physics 引数が必須 (spine_player.ts と同じ)。
  rig.skeleton.update(0)
  rig.skeleton.updateWorldTransform(Physics.update)

  // 器の大きさ = 表示高さから決める。噛みつきで頭が前へ 300 ワールド出るので横は広めに取る。
  const ow = Math.ceil(entry.targetHeightPx * 2.8 * SUPERSAMPLE)
  const oh = Math.ceil(entry.targetHeightPx * 2.0 * SUPERSAMPLE)
  if (s.canvas.width !== ow || s.canvas.height !== oh) {
    s.canvas.width = ow
    s.canvas.height = oh
  }
  const feetX = ow / 2
  const feetY = oh * FEET_Y_RATIO

  const gl = s.gl
  gl.viewport(0, 0, ow, oh)
  gl.clearColor(0, 0, 0, 0)
  gl.clear(gl.COLOR_BUFFER_BIT)

  // カメラ: ワールド(0,0)=足元 が オフスクリーンの (feetX, feetY) に来るように置く。
  s.renderer.camera.setViewport(ow, oh)
  s.renderer.camera.position.x = 0
  s.renderer.camera.position.y = feetY - oh / 2
  s.renderer.camera.update()
  s.renderer.begin()
  // 4.3.13 の drawSkeleton は pma 引数を取らない (アトラスの pma:false から自動で決まる)。
  s.renderer.drawSkeleton(rig.skeleton)
  s.renderer.end()

  // 2D 側へ等倍で貼り戻す (ctx は足元原点・facing 反転済み)。
  ctx.drawImage(s.canvas, -feetX / SUPERSAMPLE, -feetY / SUPERSAMPLE, ow / SUPERSAMPLE, oh / SUPERSAMPLE)
  return true
}
